import type { CreateMedicationLogPayload } from "@src/packages/care/hooks/useMedicationLogs";

/**
 * What a completed walk records.
 *
 * The coach's completion screen says "that's your trigger shot recorded", so
 * something has to record it. This builds the medication log that sentence is
 * promising, from the prescription the walk was resolved against
 * (`injectionCoachEligibility.ts`).
 *
 * Pure, and out of the panel, for the reason the reducer is: `CycleDetailScreen`
 * reaches `react-native` and cannot be loaded by the test runner, and a dose that
 * silently records the wrong medication — or nothing at all — is exactly the kind
 * of failure #2267 asks to keep testable. No clock either: `takenAt` and `today`
 * come in, so the record a given moment produces can be asserted.
 */

/**
 * The prescription fields a record is built from — a structural subset of
 * `ApiMedication`, so a patient medication from the API satisfies it as-is.
 */
export type CoachedDoseMedication = {
  id: string;
  /** Catalogue name first, then whatever she typed. See `injectionCoachDoses`. */
  name: string | null | undefined;
  dose?: number | null;
  doseUnit?: string | null;
  /** Patient-local times of day, `HH:MM`, as the schedule carries them. */
  timeTaken?: string[] | null;
};

/** `HH:MM`, 24-hour. Anything else is not a time we can date a dose from. */
const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d$/;

function formatDosage(medication: CoachedDoseMedication): string | null {
  if (medication.dose == null) return null;
  const unit = medication.doseUnit?.trim();
  return unit ? `${medication.dose} ${unit}` : `${medication.dose}`;
}

/**
 * The log entry for a walk that reached its last step, or null when the
 * prescription does not identify a dose well enough to record one.
 *
 * Null is a refusal, not an absence: the caller must not tell her the dose was
 * recorded when this returns nothing. A log naming no medication is worse than no
 * log — it is a row in her record that no one can act on.
 */
export function buildInjectionDoseRecord(params: {
  medication: CoachedDoseMedication | null | undefined;
  /** ISO instant the walk completed. */
  takenAt: string;
  /** Patient-local `YYYY-MM-DD`, used to date the scheduled occurrence. */
  today: string;
}): CreateMedicationLogPayload | null {
  const { medication, takenAt, today } = params;
  if (!medication?.id) return null;

  const medicationName = medication.name?.trim();
  if (!medicationName) return null;

  // Binding the log to the occurrence it belongs to is what makes the scheduled
  // dose read as taken rather than leaving a second, unattached row beside it
  // (`isMedicationLogForOccurrence`). Without a usable time we send null and the
  // matcher falls back to the day, which is the right answer for a one-off
  // trigger and the honest one when the schedule records no time.
  const scheduledTime = (medication.timeTaken ?? [])
    .map((time) => time?.trim())
    .find((time): time is string => !!time && TIME_OF_DAY.test(time));

  return {
    patientMedicationId: medication.id,
    medicationName,
    dosage: formatDosage(medication),
    takenAt,
    scheduledFor: scheduledTime ? `${today}T${scheduledTime}:00` : null,
    status: "taken",
    notes: null,
    reminderId: null,
  };
}
